"use client";

import { ArrowRight, Cpu, Radio, Settings2, Wrench } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  sn: "SVC/01",
  icon: Wrench,
  title: "Phenolic & Acrylic Plates",
  description: "Precision-engraved legend plates and nameplates using industrial-grade phenolic and two-color acrylic for high-contrast, durable identification."
}, {
  sn: "SVC/02",
  icon: Settings2,
  title: "Metal Engraving Services",
  description: "Permanent marking on stainless steel, anodized aluminum, and brass. Engineered for harsh environments and long-term asset visibility."
}, {
  sn: "SVC/03",
  icon: Radio,
  title: "Arc Flash & Safety Labels",
  description: "Compliant arc flash labels and lockout/tagout identification. Built to meet strict safety standards for electrical and industrial facilities."
}, {
  sn: "SVC/04",
  icon: Cpu,
  title: "Direct Part Marking (DPM)",
  description: "High-precision laser marking for direct part identification. Traceability solutions for components requiring permanent, machine-readable data."
}];
export default function MarkingSolutions() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="4998" id="datacentersolutionsgrid" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32 lg:py-[140px]">
      {}
      <div className="pointer-events-none absolute top-8 left-8 hidden md:block">
        <div className="relative h-4 w-4">
          <div className="bg-border absolute top-1/2 left-0 h-px w-4 -translate-y-1/2" />
          <div className="bg-border absolute top-0 left-1/2 h-4 w-px -translate-x-1/2" />
        </div>
      </div>
      <div className="pointer-events-none absolute top-8 right-8 hidden md:block">
        <div className="relative h-4 w-4">
          <div className="bg-border absolute top-1/2 left-0 h-px w-4 -translate-y-1/2" />
          <div className="bg-border absolute top-0 left-1/2 h-4 w-px -translate-x-1/2" />
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 md:px-10">
        {}
        <div ref={headerRef} className={`border-border mb-16 flex items-center justify-between border-b pb-6 transition-all duration-700 md:mb-24 ${headerVisible ? "translate-y-0 opacity-100" : "-translate-y-2 opacity-0"}`}>
          <span className="font-mono text-muted-foreground text-xs tracking-widest uppercase md:text-sm">
            § 03 / CAPABILITIES
          </span>
          <Link to="/ServicesPage" className="text-foreground hover:text-primary group font-mono text-xs tracking-widest uppercase transition-colors md:text-sm">
            <span className="inline-flex items-center gap-2">
              VIEW ALL SERVICES
              <ArrowRight className="size-3 transition-transform group-hover:translate-x-1 md:size-4" />
            </span>
          </Link>
        </div>

        {}
        <div ref={headlineRef} className={`mb-20 transition-all duration-1000 md:mb-28 ${headlineVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
          <h2 className="font-default flex flex-col text-[18vw] leading-[0.9] font-bold tracking-tighter md:text-[14vw] lg:text-[120px]">
            <span className="block">INDUSTRIAL /</span>
            <span className="block translate-x-[4vw] md:translate-x-12 lg:translate-x-16">
              MARKING /
            </span>
            <span className="block translate-x-[8vw] md:translate-x-24 lg:translate-x-32">
              SOLUTIONS.
            </span>
          </h2>
          <div className="mt-8 flex items-center gap-4 md:mt-12">
            <div className="bg-primary h-px w-12" />
            <p className="text-muted-foreground font-mono text-xs tracking-wider uppercase md:text-sm">
              Permanent. Compliant. Specified by name.
            </p>
          </div>
        </div>

        {}
        <div ref={gridRef} className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {services.map((service, i) => {
          const Icon = service.icon;
          const isOffset = i % 2 === 1;
          return <div key={service.sn} data-index={i} className={`group border-border bg-card hover:border-primary relative rounded-md border p-8 transition-all duration-500 md:p-10 lg:p-10 ${isOffset ? "lg:translate-y-6" : ""} ${gridVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`} style={{
            transitionDelay: gridVisible ? `${i * 120}ms` : "0ms"
          }}>
                {}
                <span className="bg-border group-hover:bg-primary absolute top-0 left-0 h-2 w-px transition-colors" />
                <span className="bg-border group-hover:bg-primary absolute top-0 left-0 h-px w-2 transition-colors" />
                <span className="bg-border group-hover:bg-primary absolute right-0 bottom-0 h-2 w-px transition-colors" />
                <span className="bg-border group-hover:bg-primary absolute right-0 bottom-0 h-px w-2 transition-colors" />

                {}
                <div className="text-muted-foreground group-hover:text-primary mb-8 font-mono text-xs tracking-widest transition-colors">
                  {service.sn}
                </div>

                {}
                <div className="mb-8">
                  <div className="relative inline-flex">
                    <Icon strokeWidth={1} className="text-primary size-10 transition-transform duration-500 group-hover:rotate-90" />
                    <span className="bg-primary/0 group-hover:bg-primary/10 absolute inset-0 -m-3 rounded-md blur-xl transition-all duration-500" />
                  </div>
                </div>

                {}
                <h3 className="font-default text-foreground mb-4 text-2xl leading-tight font-semibold">
                  {service.title}
                </h3>

                {}
                <p className="text-muted-foreground mb-10 text-sm leading-relaxed">
                  {service.description}
                </p>

                {}
                <Button asChild variant="link" className="text-foreground group-hover:text-primary h-auto p-0 font-mono text-xs tracking-widest uppercase no-underline hover:no-underline">
                  <Link to="/ServicesPage">
                    <span className="inline-flex items-center gap-2">
                      EXAMINE
                      <ArrowRight className="size-3 transition-transform group-hover:translate-x-1" />
                    </span>
                  </Link>
                </Button>
              </div>;
        })}
        </div>
      </div>
    </section>;
}